import { messagesOptions, useCreateMessageMutation } from '@cartwheel/api/v1'
import { Button, LoadingView, useToast } from '@cartwheel/components'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'

interface SmsPanelProps {
  clientId: string
  clientName: string
  /** Whether the client has consented to SMS — when false, sending is blocked. */
  smsConsented: boolean
}

/**
 * SMS thread panel for the patient profile page.
 *
 * Loads /v1/messages?client_id=... and renders an iMessage-style thread plus
 * a composer. The composer is disabled if the client hasn't consented to SMS
 * (matches the SmsSender backend gate).
 *
 * Polls every 10s so newly-arrived inbound SMS show up without a page refresh.
 */
export default function SmsPanel({ clientId, clientName, smsConsented }: SmsPanelProps) {
  const toast = useToast()
  const [body, setBody] = useState('')
  const messagesRef = useRef<HTMLDivElement | null>(null)

  const messagesQuery = useQuery({
    ...messagesOptions({ query: { client_id: clientId } }),
    refetchInterval: 10_000,
  })

  const sendMutation = useCreateMessageMutation({
    onSuccess: () => {
      setBody('')
      void messagesQuery.refetch()
    },
    onError: () => {
      toast.error({ description: 'Could not send the message. Please try again.' })
    },
  })

  // Auto-scroll to the newest message whenever the thread refreshes.
  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight
    }
  }, [])

  const handleSend = () => {
    if (!body.trim()) return
    sendMutation.mutate({ body: { client_id: clientId, body: body.trim() } })
  }

  if (messagesQuery.isPending) return <LoadingView title="Loading messages…" />

  const messages = (messagesQuery.data?.items ?? []) as Array<{
    id: string
    direction: string
    body: string
    sender?: { name?: string | null } | null
    sent_at?: string | null
    delivered_at?: string | null
    status: string
    created_at: string
  }>

  return (
    <div className="rounded-lg border border-gray-stroke bg-white">
      <div className="px-4 py-3 border-b border-gray-stroke flex items-center justify-between">
        <h3 className="text-sm font-semibold text-charcoal">SMS — {clientName}</h3>
        {!smsConsented && <span className="text-xs text-brick">SMS consent not on file</span>}
      </div>

      <div ref={messagesRef} className="px-4 py-3 space-y-2 max-h-72 overflow-y-auto bg-sand-10">
        {messages.length === 0 ? (
          <p className="text-sm text-charcoal-50 text-center py-6">No messages yet.</p>
        ) : (
          messages.map((m) => (
            <MessageBubble
              key={m.id}
              direction={m.direction}
              body={m.body}
              status={m.status}
              senderName={m.sender?.name}
              timestamp={m.sent_at || m.created_at}
            />
          ))
        )}
      </div>

      <div className="border-t border-gray-stroke px-4 py-3">
        <textarea
          className="w-full rounded-md border border-gray-stroke p-3 text-sm focus:outline-none focus:ring-2 focus:ring-mint focus:border-mint resize-none"
          rows={3}
          placeholder={
            smsConsented ? 'Type your message…' : 'SMS is disabled until consent is recorded.'
          }
          value={body}
          onChange={(e) => setBody(e.target.value)}
          disabled={!smsConsented}
        />
        <div className="flex justify-end gap-3 mt-2">
          <Button
            onClick={handleSend}
            disabled={!smsConsented || !body.trim() || sendMutation.isPending}
          >
            {sendMutation.isPending ? 'Sending…' : 'Send'}
          </Button>
        </div>
      </div>
    </div>
  )
}

function MessageBubble({
  direction,
  body,
  status,
  senderName,
  timestamp,
}: {
  direction: string
  body: string
  status: string
  senderName: string | null | undefined
  timestamp: string
}) {
  const outbound = direction === 'outbound'
  const formatted = new Date(timestamp).toLocaleString('en-US', {
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })

  return (
    <div className={`flex ${outbound ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap ${
          outbound ? 'bg-forest text-white' : 'bg-white border border-gray-stroke text-charcoal'
        }`}
      >
        <p>{body}</p>
        <p className={`text-xs mt-1 ${outbound ? 'text-white/80' : 'text-charcoal-50'}`}>
          {senderName ? `${senderName} • ` : ''}
          {formatted}
          {outbound && status && ` • ${status}`}
        </p>
      </div>
    </div>
  )
}
